// components/Skills.js
import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const hardSkills = [
  { name: "Cybersecurity", level: 90 },
  { name: "Mobile App Development", level: 90 },
  { name: "Web Development", level: 95 },
  { name: "React", level: 90 },
  { name: "Node.js", level: 85 },
  { name: "Python", level: 80 },
  { name: "JavaScript", level: 95 },
  { name: "MongoDB", level: 85 },
  { name: "RESTful APIs", level: 90 },
  { name: "UI/UX Design", level: 80 },
  { name: "Git & GitHub", level: 90 },
  { name: "ROS (Robot OS)", level: 75 },
];

const Skills = () => {
  const skillsRef = useRef(null);
  const stairsRef = useRef(null);
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const technicalCardRef = useRef(null);
  const professionalCardRef = useRef(null);

  const professionalSkills = [
    "Project Coordination",
    "Public Speaking",
    "Effective Communication",
    "Team Collaboration",
    "Entrepreneurial Thinking",
    "Time Management",
    "Critical Thinking",
    "Problem Solving",
    "Adaptability",
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (prefersReducedMotion) return;

      /*
        EXTRA SMOOTH STAIRS TRANSITION:
        - Stairs begin above the section.
        - They flow downward smoothly in staggered steps.
        - Content appears behind the stairs.
        - Stairs continue downward and exit smoothly.
        - scrub: 2.4 softens fast scrolling.
      */

      gsap.set(".skills-stair", {
        yPercent: -110,
      });

      gsap.set(containerRef.current, {
        opacity: 0,
        y: 80,
        scale: 0.98,
      });

      gsap.set(headerRef.current, {
        opacity: 0,
        y: 50,
      });

      gsap.set([technicalCardRef.current, professionalCardRef.current], {
        opacity: 0,
        y: 80,
        scale: 0.94,
        rotateX: 8,
        transformPerspective: 1200,
        transformOrigin: "center bottom",
      });

      gsap.set(".technical-skill-row", {
        opacity: 0,
        y: 34,
        x: -16,
      });

      gsap.set(".skill-progress-bar", {
        width: "0%",
      });

      gsap.set(".professional-skill-item", {
        opacity: 0,
        y: 42,
        scale: 0.88,
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: skillsRef.current,
          start: "top 88%",
          end: "top -18%",
          scrub: 2.4,
          invalidateOnRefresh: true,
        },
      });

      tl.to(
        ".skills-stair",
        {
          yPercent: 0,
          stagger: {
            each: 0.095,
            from: "start",
          },
          ease: "none",
        },
        0
      )

        // Content begins revealing behind the stairs
        .to(
          containerRef.current,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            ease: "none",
          },
          0.28
        )

        .to(
          headerRef.current,
          {
            opacity: 1,
            y: 0,
            ease: "none",
          },
          0.34
        )

        // Stairs continue flowing downward and exit smoothly
        .to(
          ".skills-stair",
          {
            yPercent: 115,
            stagger: {
              each: 0.095,
              from: "start",
            },
            ease: "none",
          },
          0.48
        )

        // Two main cards enter after the stairs reveal starts
        .to(
          technicalCardRef.current,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotateX: 0,
            ease: "none",
          },
          0.56
        )

        .to(
          professionalCardRef.current,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotateX: 0,
            ease: "none",
          },
          0.62
        )

        // Technical rows reveal like smaller stairs
        .to(
          ".technical-skill-row",
          {
            opacity: 1,
            y: 0,
            x: 0,
            stagger: {
              each: 0.04,
              from: "start",
            },
            ease: "none",
          },
          0.68
        )

        // Progress bars fill after the rows appear
        .to(
          ".skill-progress-bar",
          {
            width: (index) => `${hardSkills[index].level}%`,
            stagger: {
              each: 0.04,
              from: "start",
            },
            ease: "none",
          },
          0.76
        )

        // Professional skills reveal in stepped order
        .to(
          ".professional-skill-item",
          {
            opacity: 1,
            y: 0,
            scale: 1,
            stagger: {
              each: 0.05,
              from: "start",
              grid: "auto",
            },
            ease: "none",
          },
          0.78
        )

        // Final settled position
        .to(
          containerRef.current,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            ease: "none",
          },
          0.96
        );

      ScrollTrigger.refresh();
    }, skillsRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="skills"
      ref={skillsRef}
      className="section relative overflow-hidden bg-[#f4f3ee] py-16 dark:bg-black"
    >
      {/* Downward Stairs Reveal Animation */}
      <div
        ref={stairsRef}
        className="pointer-events-none absolute inset-0 z-20 flex overflow-hidden"
      >
        <div className="skills-stair h-full flex-1 bg-black dark:bg-gray-800" />
        <div className="skills-stair h-full flex-1 bg-black dark:bg-gray-700" />
        <div className="skills-stair h-full flex-1 bg-black dark:bg-gray-800" />
        <div className="skills-stair h-full flex-1 bg-black dark:bg-gray-700" />
        <div className="skills-stair h-full flex-1 bg-black dark:bg-gray-800" />
        <div className="skills-stair h-full flex-1 bg-black dark:bg-gray-700" />
      </div>

      <div
        ref={containerRef}
        className="container relative z-10 mx-auto px-6 will-change-transform"
      >
        {/* Section Header */}
        <div ref={headerRef} className="text-center mb-16 will-change-transform">
          <h2 className="section-title text-gray-900 dark:text-white">
            Skills <span className="brand-accent">&</span>{" "}
            <span className="brand-accent-green">Expertise</span>
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-lg leading-snug tracking-[-0.025em] text-gray-600 dark:text-gray-400">
            A blend of technical expertise and professional strengths to build
            secure, scalable, and innovative digital solutions.
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Technical Skills */}
          <div
            ref={technicalCardRef}
            className="brand-card-accent brand-soft-panel rounded-lg border p-8 transition-colors duration-300 will-change-transform"
          >
            <h3 className="mb-8 text-center text-3xl font-normal leading-none tracking-[-0.05em] text-gray-900 dark:text-white">
              Technical <span className="brand-accent">Skills</span>
            </h3>

            <div className="space-y-6">
              {hardSkills.map((skill, index) => (
                <div
                  key={index}
                  className="technical-skill-row will-change-transform"
                >
                  <div className="flex justify-between mb-2">
                    <span className="font-semibold uppercase tracking-[0.1em] text-gray-800 dark:text-gray-200 text-xs">
                      {skill.name}
                    </span>

                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {skill.level}%
                    </span>
                  </div>

                  <div className="h-px w-full overflow-hidden bg-black/12 dark:bg-white/12">
                    <div className="skill-progress-bar brand-rule h-px" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Professional Skills */}
          <div
            ref={professionalCardRef}
            className="brand-card-accent brand-soft-panel rounded-lg border p-8 transition-colors duration-300 will-change-transform"
          >
            <h3 className="mb-8 text-center text-3xl font-normal leading-none tracking-[-0.05em] text-gray-900 dark:text-white">
              Professional <span className="brand-accent-green">Skills</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {professionalSkills.map((skill, index) => (
                <div
                  key={index}
                  className="professional-skill-item brand-chip flex items-center justify-center rounded-full border bg-transparent p-4 text-center text-xs font-semibold uppercase tracking-[0.12em] transition-transform duration-300 hover:border-[var(--brand-green)] hover:text-[var(--brand-green)] will-change-transform"
                >
                  {skill}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
